// data-fit LLC · DecisionFlow · Amendment Governance
// AmendmentDeltaPanel.jsx · API: v1.27 · S247 Phase 10
// OI-AMENDMENT-UI-DELTA-PANEL
//
// Shown after a preview rerun completes. Calls GET /api/v1/calculation-runs/delta
// and renders: severity badge, net delta summary, per-rep rows.
//
// Props:
//   fiscalPeriodId   number   — used to scope delta query
//   previewRunId     number   — the calculation_run_id of the completed preview run
//   onDismiss        fn()     — optional, shown if provided
//
// Visibility: comp_admin only — caller is responsible for gating.
// NN-03 / NN-04: read-only display. No mutation.

import React, { useEffect, useMemo, useState } from 'react'

const API_BASE = '/api/v1'

function parseMoney(v) { const n = Number(v); return Number.isFinite(n) ? n : 0 }

function formatMoney(v) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency', currency: 'USD', maximumFractionDigits: 2,
  }).format(parseMoney(v))
}

function formatDelta(v) {
  const n = parseMoney(v)
  const sign = n >= 0 ? '+' : ''
  return `${sign}${formatMoney(n)}`
}

// Severity colours match spec: amber for moderate, red for significant, green for minor/none
function SeverityBadge({ severity }) {
  const map = {
    significant: 'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))] border-[rgb(var(--color-danger))]/30',
    moderate:    'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))] border-[rgb(var(--color-warning))]/30',
    minor:       'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))] border-[rgb(var(--color-success))]/30',
    none:        'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] border-[rgb(var(--color-border))]',
  }
  const label = severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : 'Unknown'
  const cls = map[severity] ?? map.none
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-tok-xs font-semibold ${cls}`}>
      {label}
    </span>
  )
}

function DeltaCell({ value }) {
  const n = parseMoney(value)
  const colour = n > 0
    ? 'text-[rgb(var(--color-success))]'
    : n < 0
    ? 'text-[rgb(var(--color-danger))]'
    : 'text-[rgb(var(--color-text-muted))]'
  return <span className={`font-mono text-tok-sm ${colour}`}>{formatDelta(value)}</span>
}

export default function AmendmentDeltaPanel({ fiscalPeriodId, previewRunId, onDismiss }) {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)

  const periodId = useMemo(() => {
    const n = Number(fiscalPeriodId); return Number.isInteger(n) ? n : null
  }, [fiscalPeriodId])

  const load = async () => {
    console.log('AmendmentDeltaPanel load — previewRunId:', previewRunId, 'periodId:', periodId)
    if (!periodId) { setError('No fiscal period — cannot load delta.'); return }
    if (!previewRunId) { setError('No preview run ID — cannot load delta.'); return }
    setLoading(true); setError(null)
    try {
      // Step 1 — find the last completed final run for this period (from_run_id)
      const listRes = await fetch(
        `${API_BASE}/calculation-runs?fiscal_period_id=${periodId}&run_type=final&run_status=completed&limit=1`,
        { credentials: 'include' }
      )
      const listPayload = await listRes.json().catch(() => ({}))
      const runs = listPayload?.data ?? listPayload?.calculation_runs ?? listPayload?.runs ?? []
      const fromRunId = runs[0]?.calculation_run_id ?? runs[0]?.id ?? null

      if (!fromRunId) {
        // No prior final run — show preview run vs itself (zero delta) or explain
        setError('No prior final run found for this period. Run a final calculation first to see delta.')
        return
      }

      // Step 2 — call delta with from_run_id (last final) and to_run_id (preview)
      const params = new URLSearchParams({
        from_run_id: String(fromRunId),
        to_run_id:   String(previewRunId),
      })
      const res = await fetch(`${API_BASE}/calculation-runs/delta?${params.toString()}`, {
        credentials: 'include',
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) {
        const err = payload?.error ?? payload?.detail?.error
        const msg = err?.message || err?.recovery_action || `Error ${res.status}`
        setError(msg); return
      }
      setData(payload)
    } catch (e) {
      setError(e.message || 'Network error loading delta.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [periodId, previewRunId])

  const rows     = data?.rows ?? data?.delta_rows ?? []
  const summary  = data?.summary ?? {}
  const severity = data?.severity ?? summary?.severity ?? null
  const netDelta = summary?.net_delta ?? data?.net_delta ?? null

  return (
    <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 flex flex-col gap-4">

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-warning))] mb-1">
            Amendment Impact
          </p>
          <h3 className="text-tok-base font-bold text-[rgb(var(--color-text))]">Preview Delta Review</h3>
          <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Payout changes resulting from committed amendments vs. last final run.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {severity && <SeverityBadge severity={severity} />}
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
              aria-label="Dismiss delta panel"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="animate-pulse space-y-2">
          <div className="h-4 w-1/3 rounded bg-[rgb(var(--color-surface-muted))]" />
          <div className="h-4 w-2/3 rounded bg-[rgb(var(--color-surface-muted))]" />
          <div className="h-4 w-1/2 rounded bg-[rgb(var(--color-surface-muted))]" />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))] flex items-center justify-between gap-3">
          {error}
          <button type="button" onClick={load} className="shrink-0 text-tok-xs underline hover:no-underline">Retry</button>
        </div>
      )}

      {/* Net delta summary */}
      {!loading && !error && netDelta !== null && (
        <div className="flex items-center gap-4 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-0.5">Net payout delta</p>
            <DeltaCell value={netDelta} />
          </div>
          {summary?.positions_affected != null && (
            <div className="border-l border-[rgb(var(--color-border))] pl-4">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-0.5">Positions affected</p>
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{summary.positions_affected}</p>
            </div>
          )}
          {summary?.positions_added != null && summary.positions_added > 0 && (
            <div className="border-l border-[rgb(var(--color-border))] pl-4">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-0.5">Positions added</p>
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">{summary.positions_added}</p>
            </div>
          )}
          {summary?.positions_removed != null && summary.positions_removed > 0 && (
            <div className="border-l border-[rgb(var(--color-border))] pl-4">
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mb-0.5">Positions removed</p>
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-danger))]">{summary.positions_removed}</p>
            </div>
          )}
        </div>
      )}

      {/* No data yet */}
      {!loading && !error && data === null && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">Delta data not yet available.</p>
      )}

      {/* No rows */}
      {!loading && !error && data !== null && rows.length === 0 && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No per-rep delta rows returned — payouts are unchanged.</p>
      )}

      {/* Per-rep rows table */}
      {!loading && !error && rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
          <table className="min-w-full divide-y divide-[rgb(var(--color-border))] text-tok-sm">
            <thead className="bg-[rgb(var(--color-surface-muted))]">
              <tr>
                {['Position', 'Prev Total', 'New Total', 'Delta', 'Status'].map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-tok-xs font-medium text-[rgb(var(--color-text-muted))] uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--color-border))]">
              {rows.map((row, i) => (
                <tr key={row.position_id ?? i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                  <td className="px-4 py-2 text-[rgb(var(--color-text))]">
                    {row.full_name ?? row.position_id ?? '—'}
                    {row.full_name && row.position_id && (
                      <span className="ml-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">#{row.position_id}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-[rgb(var(--color-text))]">
                    {row.prev_total != null ? formatMoney(row.prev_total) : <span className="text-[rgb(var(--color-text-muted))]">—</span>}
                  </td>
                  <td className="px-4 py-2 text-[rgb(var(--color-text))]">
                    {row.new_total != null ? formatMoney(row.new_total) : <span className="text-[rgb(var(--color-text-muted))]">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    <DeltaCell value={row.delta ?? row.payout_delta} />
                  </td>
                  <td className="px-4 py-2">
                    {row.position_status === 'added' && (
                      <span className="inline-flex rounded-full bg-[rgb(var(--color-success))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-success))]">Added</span>
                    )}
                    {row.position_status === 'removed' && (
                      <span className="inline-flex rounded-full bg-[rgb(var(--color-danger))]/10 px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-danger))]">Removed</span>
                    )}
                    {(!row.position_status || row.position_status === 'changed') && (
                      <span className="inline-flex rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Changed</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Governance note */}
      <p className="text-tok-xs text-[rgb(var(--color-text-muted))] border-t border-[rgb(var(--color-border))] pt-3">
        Review the delta above before reapproving stages. Reapproval is required per stage — use the Reapprove button on each flagged stage.
        The final run will remain blocked until all <strong>requires_reapproval</strong> stages are cleared.
      </p>
    </div>
  )
}
